<template>
    <div class="ei-bridge-bar">
        <div v-if="!hasAnyValue" class="ei-bridge-bar-empty">표시할 원단위 데이터가 없습니다</div>
        <div v-show="hasAnyValue" ref="chartEl" class="ei-bridge-bar-canvas"></div>
    </div>
</template>

<script>
import * as echarts from "echarts";
import { ymShortLabel, enIntBridgeTooltipBase, enIntBridgeTooltipCardHtml } from "../utils/enIntUi.js";

const TEAL = "#0e7490";
const GRAY = "#cbd5e1";
const AVG_LINE = "#0f766e";

export default {
    name: "EnIntBridgeBarChart",
    props: {
        getMasterSummaryApiState: {
            type: Object,
            default: () => ({ response: null, data: null, loading: false, error: "" }),
        },
        getMonthlySeriesApiState: {
            type: Object,
            default: () => ({ response: null, data: null, loading: false, error: "" }),
        },
        intensityUnit: { type: String, default: "" },
    },
    data() {
        return { chart: null };
    },
    computed: {
        barSeries() {
            const months = Array.isArray(this.getMonthlySeriesApiState?.data?.months)
                ? [...this.getMonthlySeriesApiState.data.months]
                : [];
            const categories = [];
            const values = [];
            const momValues = [];
            const yoyValues = [];
            for (const monthRow of months) {
                const monthYm = Number(monthRow.regYm);
                categories.push(ymShortLabel(monthYm));
                const monthValue = monthRow?.value;
                values.push(monthValue != null && Number.isFinite(Number(monthValue)) ? Number(monthValue) : null);
                const momValue = monthRow?.mom?.prevVal;
                momValues.push(momValue != null && Number.isFinite(Number(momValue)) ? Number(momValue) : null);
                const yoyValue = monthRow?.yoy?.yoyVal;
                yoyValues.push(yoyValue != null && Number.isFinite(Number(yoyValue)) ? Number(yoyValue) : null);
            }
            let maxIdx = -1;
            let maxVal = -Infinity;
            values.forEach((value, idx) => {
                if (value != null && value > maxVal) {
                    maxVal = value;
                    maxIdx = idx;
                }
            });
            return { categories, values, momValues, yoyValues, maxIdx };
        },
        avgValue() {
            const value = Number(this.getMasterSummaryApiState?.data?.arithmeticMeanCalcVal);
            return Number.isFinite(value) ? value : null;
        },
        hasAnyValue() {
            const hasBase = (this.barSeries?.values || []).some((v) => v != null && Number.isFinite(Number(v)));
            const hasMom = (this.barSeries?.momValues || []).some((v) => v != null && Number.isFinite(Number(v)));
            const hasYoy = (this.barSeries?.yoyValues || []).some((v) => v != null && Number.isFinite(Number(v)));
            return hasBase || hasMom || hasYoy;
        },
    },
    watch: {
        getMasterSummaryApiState: { deep: true, handler: "scheduleRender" },
        getMonthlySeriesApiState: { deep: true, handler: "scheduleRender" },
        intensityUnit: "scheduleRender",
    },
    mounted() {
        this.scheduleRender();
    },
    beforeDestroy() {
        this.detachBarHover();
        window.removeEventListener("resize", this.onResize);
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    methods: {
        scheduleRender() {
            this.$nextTick(() => {
                if (!this.hasAnyValue) {
                    this.detachBarHover();
                    if (this.chart) {
                        this.chart.dispose();
                        this.chart = null;
                    }
                    return;
                }
                this.renderOrUpdate();
            });
        },
        onResize() {
            if (this.chart) this.chart.resize();
        },
        detachBarHover() {
            if (!this.chart) return;
            if (this._onBarMouseOver) this.chart.off("mouseover", this._onBarMouseOver);
            if (this._onBarMouseOut) this.chart.off("mouseout", this._onBarMouseOut);
            if (this._onBarGlobalOut) this.chart.off("globalout", this._onBarGlobalOut);
            this._onBarMouseOver = null;
            this._onBarMouseOut = null;
            this._onBarGlobalOut = null;
        },
        attachBarHover(categories, rawValues) {
            this.detachBarHover();
            this._onBarMouseOver = (params) => {
                if (params.componentType !== "series" || params.seriesType !== "bar") return;
                const di = params.dataIndex;
                const orig = rawValues[di];
                if (orig == null || !Number.isFinite(Number(orig))) return;
                this.$emit("bar-hover", {
                    value: Number(orig),
                    dataIndex: di,
                    category: categories[di] || "",
                });
            };
            this._onBarMouseOut = (params) => {
                if (params.componentType === "series" && params.seriesType === "bar") {
                    this.$emit("bar-hover-end");
                }
            };
            this._onBarGlobalOut = () => this.$emit("bar-hover-end");
            this.chart.on("mouseover", this._onBarMouseOver);
            this.chart.on("mouseout", this._onBarMouseOut);
            this.chart.on("globalout", this._onBarGlobalOut);
        },
        renderOrUpdate() {
            const el = this.$refs.chartEl;
            if (!el) return;

            const w = el.clientWidth;
            const h = el.clientHeight;
            if (w < 8 || h < 8) {
                requestAnimationFrame(() => this.renderOrUpdate());
                return;
            }

            const { categories, values, momValues, yoyValues, maxIdx } = this.barSeries;
            const rawValues = values;
            const hasMom = (momValues || []).some((v) => v != null && Number.isFinite(Number(v)));
            const hasYoy = (yoyValues || []).some((v) => v != null && Number.isFinite(Number(v)));
            const avg = Number(this.avgValue);
            const hasAvg = Number.isFinite(avg);
            const labelColor = "#64748b";

            if (!this.chart) {
                this.chart = echarts.init(el, null, { renderer: "canvas" });
                window.addEventListener("resize", this.onResize);
            }

            const tooltipRow = (label, val, unit) => {
                if (val == null || !Number.isFinite(Number(val))) {
                    return { label, value: "—" };
                }
                const num = Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 });
                return { label, value: unit ? `${num} ${unit}` : num };
            };

            const baseBarSeries = {
                name: "기준",
                type: "bar",
                data: values.map((v, i) => ({
                    value: v != null ? v : 0,
                    itemStyle: {
                        color: v == null ? "rgba(0,0,0,0)" : i === maxIdx && maxIdx >= 0 ? TEAL : GRAY,
                        borderRadius: v == null ? 0 : [3, 3, 0, 0],
                    },
                })),
                barMaxWidth: 22,
                z: 2,
            };

            if (hasAvg) {
                baseBarSeries.markLine = {
                    silent: true,
                    symbol: ["none", "none"],
                    label: { show: false },
                    lineStyle: {
                        color: AVG_LINE,
                        width: 1.25,
                        type: "solid",
                        opacity: 0.8,
                    },
                    emphasis: { disabled: true },
                    animation: false,
                    data: [{ yAxis: avg }],
                };
            }

            const series = [baseBarSeries];
            if (hasMom) {
                series.push({
                    name: "전월",
                    type: "line",
                    data: momValues,
                    connectNulls: false,
                    showSymbol: false,
                    lineStyle: { width: 1.6, color: "#475569", type: "dashed" },
                    itemStyle: { color: "#475569" },
                    z: 4,
                });
            }
            if (hasYoy) {
                series.push({
                    name: "전년",
                    type: "line",
                    data: yoyValues,
                    connectNulls: false,
                    showSymbol: false,
                    lineStyle: { width: 1.4, color: "#94a3b8", type: "dotted" },
                    itemStyle: { color: "#94a3b8" },
                    z: 3,
                });
            }

            this.chart.setOption(
                {
                    animationDuration: 280,
                    grid: { left: 4, right: 4, top: 8, bottom: 18, containLabel: false },
                    tooltip: enIntBridgeTooltipBase({
                        trigger: "axis",
                        axisPointer: {
                            type: "shadow",
                            shadowStyle: { color: "rgba(14, 116, 144, 0.1)" },
                        },
                        formatter: (params) => {
                            const p = Array.isArray(params) ? params[0] : null;
                            const dataIndex = p?.dataIndex ?? 0;
                            const orig = rawValues[dataIndex];
                            const mom = momValues[dataIndex];
                            const yoy = yoyValues[dataIndex];
                            const u = (this.intensityUnit || "").trim();
                            const period = p?.axisValue != null ? String(p.axisValue) : "";
                            if (
                                (orig == null || !Number.isFinite(Number(orig))) &&
                                (mom == null || !Number.isFinite(Number(mom))) &&
                                (yoy == null || !Number.isFinite(Number(yoy))) &&
                                !hasAvg
                            ) {
                                return enIntBridgeTooltipCardHtml({
                                    heading: period || "해당 월",
                                    muted: "데이터 없음",
                                });
                            }
                            const rows = [
                                tooltipRow("기준", orig, u),
                                tooltipRow("전월", mom, u),
                                tooltipRow("전년", yoy, u),
                            ];
                            if (hasAvg) rows.push(tooltipRow("평균", avg, u));
                            return enIntBridgeTooltipCardHtml({
                                heading: period,
                                rows,
                            });
                        },
                    }),
                    xAxis: {
                        type: "category",
                        data: categories,
                        axisLabel: {
                            color: labelColor,
                            fontSize: 10,
                            interval: 0,
                            formatter: (value, index) => (index === maxIdx && maxIdx >= 0 ? value : ""),
                        },
                        axisTick: { show: false },
                        axisLine: { show: false },
                    },
                    yAxis: {
                        type: "value",
                        show: false,
                        scale: true,
                        axisLine: { show: false },
                        axisTick: { show: false },
                        splitLine: { show: false },
                    },
                    series,
                },
                true,
            );

            this.attachBarHover(categories, rawValues);

            requestAnimationFrame(() => {
                this.chart?.resize();
            });
        },
    },
};
</script>

<style scoped>
.ei-bridge-bar {
    width: 100%;
    min-height: 118px;
    font-size: inherit;
    line-height: inherit;
}

.ei-bridge-bar-canvas {
    width: 100%;
    height: 118px;
}

.ei-bridge-bar-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;
    padding: 12px;
    font-size: var(--ei-bridge-font-subtle, 10px);
    font-weight: 600;
    color: #94a3b8;
    text-align: center;
    border: 1px dashed rgba(203, 213, 225, 0.95);
    border-radius: 8px;
    background: rgba(248, 250, 252, 0.8);
}
</style>
